import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Calendar, MapPin, ExternalLink, Check, Globe, Building2, Users } from "lucide-react";
import { toast } from "sonner";

const STATES = ["ALL", "NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];
const CATEGORY_FILTERS = ["All", "University", "Workshop", "Career", "Wellbeing", "Social"];

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-AU", {
      weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function Events() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [mine, setMine] = useState([]);
  const [state, setState] = useState("ALL");
  const [cat, setCat] = useState("All");
  const [online, setOnline] = useState("any"); // any|online|inperson

  const load = async () => {
    const params = new URLSearchParams();
    if (state !== "ALL") params.set("state", state);
    if (cat !== "All") params.set("category", cat);
    if (online !== "any") params.set("online", online === "online" ? "true" : "false");
    const [a, b] = await Promise.all([
      api.get(`/events?${params.toString()}`),
      api.get("/events/mine/list"),
    ]);
    setEvents(a.data); setMine(b.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [state, cat, online]);

  const isRsvped = (id) => mine.some(m => m.event_id === id);

  const rsvp = async (id) => {
    try {
      await api.post("/events/rsvp", { event_id: id });
      toast.success("You're going! 🎉");
      load();
    } catch { toast.error("Couldn't RSVP"); }
  };
  const cancel = async (id) => {
    try {
      await api.delete(`/events/rsvp/${id}`);
      toast("Cancelled RSVP");
      load();
    } catch { toast.error("Couldn't cancel"); }
  };

  return (
    <div data-testid="events-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Local events</h1>
          <p className="text-white/60 mt-1.5">Workshops, expos, meetups & uni events near you.</p>
        </div>
        {mine.length > 0 && (
          <div className="text-sm text-neongreen flex items-center gap-1.5"><Check size={14}/> {mine.length} event{mine.length === 1 ? "" : "s"} booked</div>
        )}
      </div>

      <div className="glass-card p-4 mt-5 flex items-start gap-3 border-neoncyan/20" data-testid="events-disclaimer">
        <Globe size={18} className="text-neoncyan flex-shrink-0 mt-0.5"/>
        <div className="text-xs text-white/70 leading-relaxed">
          <strong className="text-white">Heads up:</strong> Sample events shown to demonstrate. Each one links to the
          organiser's <strong>live calendar</strong> (USyd, Monash, Tenants Vic, ATO, etc.) — click through to find the
          current dates and register. Want your event listed? <a href="mailto:partners@adulting101.com" className="text-neoncyan underline">Email us</a>.
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-6">
        <span className="text-xs uppercase tracking-widest text-white/45 self-center pr-1">State</span>
        {STATES.map(s => (
          <button key={s} onClick={() => setState(s)} data-testid={`event-state-${s}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${state === s ? "bg-neonpink text-white shadow-glow" : "bg-white/5 text-white/70 border border-white/10"}`}>
            {s === "ALL" ? "All states" : s}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <span className="text-xs uppercase tracking-widest text-white/45 self-center pr-1">Type</span>
        {CATEGORY_FILTERS.map(c => (
          <button key={c} onClick={() => setCat(c)} data-testid={`event-cat-${c}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${cat === c ? "bg-neoncyan text-ink shadow-glowcyan" : "bg-white/5 text-white/70 border border-white/10"}`}>
            {c}
          </button>
        ))}
        <span className="text-xs uppercase tracking-widest text-white/45 self-center pr-1 ml-3">Where</span>
        {[["any", "Any"], ["online", "Online"], ["inperson", "In person"]].map(([k, l]) => (
          <button key={k} onClick={() => setOnline(k)} data-testid={`event-mode-${k}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${online === k ? "bg-neongreen text-ink shadow-glowgreen" : "bg-white/5 text-white/70 border border-white/10"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-7">
        {events.map(e => (
          <div key={e.event_id} className="glass-card overflow-hidden flex flex-col" data-testid={`event-card-${e.event_id}`}>
            {e.cover_image && (
              <div className="aspect-[16/9] relative">
                <img src={e.cover_image} alt={e.title} className="w-full h-full object-cover"/>
                <div className="absolute inset-0 bg-gradient-to-t from-ink to-transparent"/>
                <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-neonpink text-white text-xs font-bold">{e.category}</span>
                {e.online && <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-neoncyan text-ink text-xs font-bold flex items-center gap-1"><Globe size={10}/>Online</span>}
              </div>
            )}
            <div className="p-5 flex-1 flex flex-col">
              <div className="font-display font-bold text-lg leading-tight">{e.title}</div>
              <div className="text-xs text-white/55 mt-1.5 flex items-center gap-1.5"><Building2 size={12}/> {e.organiser}</div>
              <div className="text-sm text-white/70 mt-3 line-clamp-3 flex-1">{e.description}</div>
              <div className="mt-4 space-y-1.5 text-xs text-white/60">
                <div className="flex items-center gap-2"><Calendar size={12}/> {formatDate(e.starts_at)}</div>
                <div className="flex items-center gap-2"><MapPin size={12}/> {e.venue}, {e.city} {e.state !== "ALL" && `(${e.state})`}</div>
                {e.free && <div className="flex items-center gap-2 text-neongreen"><Users size={12}/> Free</div>}
              </div>
              <div className="flex gap-2 mt-4">
                {isRsvped(e.event_id) ? (
                  <button onClick={() => cancel(e.event_id)} data-testid={`event-cancel-${e.event_id}`}
                    className="btn-secondary-neon text-xs flex-1 flex items-center justify-center gap-1">
                    <Check size={12}/> Going · Cancel
                  </button>
                ) : (
                  <button onClick={() => rsvp(e.event_id)} data-testid={`event-rsvp-${e.event_id}`}
                    className="btn-primary-neon text-xs flex-1">RSVP</button>
                )}
                {e.url && (
                  <a href={e.url} target="_blank" rel="noopener noreferrer" data-testid={`event-link-${e.event_id}`}
                    className="px-3 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center">
                    <ExternalLink size={14}/>
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
        {events.length === 0 && (
          <div className="col-span-full glass-card p-8 text-center text-white/60">
            No events match your filters. Try widening them.
          </div>
        )}
      </div>
    </div>
  );
}
